import NumberInput from '../../../components/NumberInput'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Check, Loader2, Search, X } from 'lucide-react'
import { Button } from '../../../components/ui/button'
import { cn } from '../../../lib/utils'
import { getMyFoodsGroupedForMeal, pickName, hasNaturalUnit, unitLabel } from '../../../lib/myFoods'

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack']

function round1(n) {
  if (n == null || !Number.isFinite(n)) return 0
  return Math.round(n * 10) / 10
}

// Scale a per-100g preset to an arbitrary portion size in grams.
function scaleFood(food, portionG) {
  const factor = (Number(portionG) || 0) / 100
  return {
    kcal: Math.round((food.kcal_per_100g || 0) * factor),
    protein: round1((food.protein_per_100g || 0) * factor),
    carbs: round1((food.carbs_per_100g || 0) * factor),
    fat: round1((food.fat_per_100g || 0) * factor),
  }
}

export function MealBuilder({
  defaultDate,
  defaultType = 'breakfast',
  onSubmit,
  onSwitchToManual,
  onCancel,
  submitting,
}) {
  const { t, i18n } = useTranslation()
  const today = new Date().toLocaleDateString('en-CA')

  const [mealType, setMealType] = useState(defaultType)
  const [date, setDate] = useState(defaultDate || today)
  const [query, setQuery] = useState('')
  // items: { food, portionG } — tracks each selected preset with its
  // (possibly user-edited) portion size.
  const [items, setItems] = useState([])

  const { suggested, others } = useMemo(() => getMyFoodsGroupedForMeal(mealType), [mealType])

  const matchesQuery = (food) => {
    const q = query.trim().toLowerCase()
    if (!q) return true
    const en = (food.name_en || '').toLowerCase()
    const ar = food.name_ar || ''
    return en.includes(q) || ar.includes(query.trim())
  }

  // Flat ordered list used for rendering: section labels interleaved with
  // food entries. Keeps the rendering loop simple while preserving the
  // visual divider between "suggested for this slot" and "everything else".
  // When the query is non-empty we hide section headings for empty groups
  // so we don't render a dangling label with nothing under it.
  const renderList = useMemo(() => {
    const out = []
    const filteredSuggested = suggested.filter(matchesQuery)
    const filteredOthers = others.filter(matchesQuery)
    if (filteredSuggested.length > 0) {
      out.push({ kind: 'heading', key: 'suggested', label: t('phoenixTracker.meals.builder.suggestedHeading', { slot: t(`phoenixTracker.meals.types.${mealType}`) }) })
      for (const f of filteredSuggested) out.push({ kind: 'food', food: f })
    }
    if (filteredOthers.length > 0) {
      out.push({ kind: 'heading', key: 'others', label: t('phoenixTracker.meals.builder.otherFoodsHeading') })
      for (const f of filteredOthers) out.push({ kind: 'food', food: f })
    }
    return out
  }, [suggested, others, mealType, query, t])

  const isRecipe = (food) => Array.isArray(food?.recipe) && food.recipe.length > 0

  const isSelected = (food) => {
    if (isRecipe(food)) return items.some((i) => i.recipeId === food.name_en)
    return items.some((i) => i.food === food)
  }
  const getItem = (food) => items.find((i) => i.food === food)

  const toggleFood = (food) => {
    if (isRecipe(food)) {
      const recipeId = food.name_en
      setItems((prev) => {
        if (prev.some((i) => i.recipeId === recipeId)) {
          return prev.filter((i) => i.recipeId !== recipeId)
        }
        const expanded = food.recipe.map((ing) => ({
          food: ing,
          portionG: ing.default_g || 100,
          recipeId,
          recipeName: pickName(food, i18n.language),
        }))
        return [...prev, ...expanded]
      })
      return
    }
    setItems((prev) => {
      if (prev.find((i) => i.food === food)) {
        return prev.filter((i) => i.food !== food)
      }
      return [...prev, { food, portionG: food.typical_portion_g || 100 }]
    })
  }

  const updatePortion = (food, portionG) => {
    setItems((prev) =>
      prev.map((i) => (i.food === food ? { ...i, portionG } : i))
    )
  }

  const totals = useMemo(() => {
    return items.reduce(
      (acc, item) => {
        const m = scaleFood(item.food, item.portionG)
        return {
          kcal: acc.kcal + m.kcal,
          protein: acc.protein + m.protein,
          carbs: acc.carbs + m.carbs,
          fat: acc.fat + m.fat,
        }
      },
      { kcal: 0, protein: 0, carbs: 0, fat: 0 }
    )
  }, [items])

  // Switching meal type wipes the selection — a breakfast item still
  // selected when you flip to lunch would silently end up in the wrong
  // meal slot, which is worse than starting over.
  const handleMealTypeChange = (mt) => {
    setMealType(mt)
    setItems([])
  }

  const buildName = () => {
    const seenRecipes = new Set()
    const names = []
    for (const i of items) {
      if (i.recipeId) {
        if (seenRecipes.has(i.recipeId)) continue
        seenRecipes.add(i.recipeId)
        names.push(i.recipeName || i.recipeId)
      } else {
        names.push(pickName(i.food, i18n.language))
      }
    }
    const joined = names.join(' + ')
    if (joined.length <= 120) return joined
    return `${t(`phoenixTracker.meals.types.${mealType}`)} (${names.length})`
  }

  const handleSave = async () => {
    if (items.length === 0) return
    await onSubmit({
      meal_type: mealType,
      date,
      name: buildName(),
      kcal: totals.kcal,
      protein_g: round1(totals.protein),
      carbs_g: round1(totals.carbs),
      fat_g: round1(totals.fat),
      notes: '',
    })
  }

  return (
    <div className="space-y-3">
      {/* Date */}
      <div>
        <label className="block text-xs text-muted-foreground mb-1">
          {t('phoenixTracker.weight.date')}
        </label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          required
          dir="ltr"
          className="h-11 w-full px-3 rounded-lg border border-input bg-background text-foreground text-base sm:text-sm font-mono"
        />
      </div>

      {/* Meal type */}
      <div>
        <label className="block text-xs text-muted-foreground mb-1.5">
          {t('phoenixTracker.meals.form.type')}
        </label>
        <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1">
          {MEAL_TYPES.map((mt) => (
            <button
              key={mt}
              type="button"
              onClick={() => handleMealTypeChange(mt)}
              className={cn(
                'h-9 px-3 rounded-full text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0',
                mealType === mt
                  ? 'bg-app-primary-500 text-white'
                  : 'bg-muted text-muted-foreground hover:bg-accent'
              )}
            >
              {t(`phoenixTracker.meals.types.${mt}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('phoenixTracker.meals.builder.searchPlaceholder')}
          aria-label={t('phoenixTracker.meals.builder.searchPlaceholder')}
          className="h-10 w-full ps-9 pe-9 rounded-lg border border-input bg-background text-foreground text-sm"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            aria-label={t('phoenixTracker.meals.builder.clearSearch')}
            className="absolute top-1/2 -translate-y-1/2 end-2 p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Foods list — suggested for this slot first, then everything else */}
      <div>
        {renderList.length === 0 ? (
          <div className="rounded-lg border border-dashed border-input p-4 text-center text-xs text-muted-foreground">
            {query
              ? t('phoenixTracker.meals.builder.noSearchResults', { query: query.trim() })
              : t('phoenixTracker.meals.builder.noFoods')}
          </div>
        ) : (
          <div className="space-y-1.5">
            {renderList.map((item) => {
              if (item.kind === 'heading') {
                return (
                  <p
                    key={item.key}
                    className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold px-1 pt-2 first:pt-0"
                    dir="auto"
                  >
                    {item.label}
                  </p>
                )
              }
              const food = item.food
              const selected = isSelected(food)
              const itemState = getItem(food)
              const recipeFood = isRecipe(food)
              const recipeItems = recipeFood
                ? items.filter((i) => i.recipeId === food.name_en)
                : null
              // For a selected recipe, derive the displayed totals from the
              // live ingredient grams so the user sees their adjustments
              // immediately reflected on the recipe card.
              const portionG = recipeFood && selected
                ? recipeItems.reduce((sum, i) => sum + (Number(i.portionG) || 0), 0)
                : (itemState?.portionG ?? food.typical_portion_g)
              const macros = recipeFood && selected
                ? recipeItems.reduce(
                    (acc, i) => {
                      const m = scaleFood(i.food, i.portionG)
                      return {
                        kcal: acc.kcal + m.kcal,
                        protein: round1(acc.protein + m.protein),
                        carbs: round1(acc.carbs + m.carbs),
                        fat: round1(acc.fat + m.fat),
                      }
                    },
                    { kcal: 0, protein: 0, carbs: 0, fat: 0 }
                  )
                : scaleFood(food, portionG)
              const natural = !recipeFood && hasNaturalUnit(food)
              const count = natural
                ? Math.round((portionG / food.grams_per_unit) * 10) / 10
                : null
              return (
                <div
                  key={food.name_en}
                  className={cn(
                    'rounded-lg border transition-colors',
                    selected
                      ? 'border-app-primary-500 bg-app-primary-500/5'
                      : 'border-input bg-background'
                  )}
                >
                  <button
                    type="button"
                    onClick={() => toggleFood(food)}
                    className="w-full flex items-center gap-3 p-3 text-start"
                  >
                    <div
                      className={cn(
                        'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors',
                        selected
                          ? 'border-app-primary-500 bg-app-primary-500'
                          : 'border-input bg-background'
                      )}
                    >
                      {selected && (
                        <Check className="w-3 h-3 text-white" strokeWidth={3} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <p
                          className="text-sm font-medium text-foreground truncate"
                          dir="auto"
                        >
                          {pickName(food, i18n.language)}
                        </p>
                        {food.category === 'treat' && (
                          <span
                            className="px-1.5 py-0.5 rounded bg-app-primary-100 dark:bg-app-primary-900/30 text-app-primary-700 dark:text-app-primary-300 text-[10px] font-medium flex-shrink-0"
                            dir="auto"
                          >
                            {t('phoenixTracker.meals.treatBadge')}
                          </span>
                        )}
                      </div>
                      {/* Portion + macros: dir="auto" picks RTL when the unit
                          label is Arabic; each numeric chunk is bdi-isolated
                          so it doesn't get split by the bidi algorithm. */}
                      <p
                        className="text-[11px] text-muted-foreground font-mono mt-0.5"
                        dir="auto"
                      >
                        {natural && (
                          <>
                            <bdi>
                              {count % 1 === 0 ? count : count.toFixed(1)}{' '}
                              {unitLabel(food, count, i18n.language)}
                            </bdi>
                            <span className="mx-1">·</span>
                          </>
                        )}
                        <bdi>{Math.round(portionG)} g</bdi>
                        <span className="mx-1">·</span>
                        <bdi>{macros.kcal} kcal</bdi>
                        <span className="mx-1">·</span>
                        <bdi>{macros.protein}P / {macros.carbs}C / {macros.fat}F</bdi>
                      </p>
                    </div>
                  </button>
                  {selected && recipeFood && (
                    <div className="px-3 pb-3 pt-0 ms-7 space-y-2 border-s-2 border-app-primary-500/30 ps-3">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold" dir="auto">
                        {t('phoenixTracker.meals.builder.recipeIngredients')}
                      </p>
                      {food.recipe.map((ing) => {
                        const ingItem = items.find(
                          (i) => i.recipeId === food.name_en && i.food === ing
                        )
                        const ingPortion = ingItem?.portionG ?? ing.default_g ?? 0
                        const ingMacros = scaleFood(ing, ingPortion)
                        return (
                          <div key={ing.name_en} className="flex items-center gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-foreground truncate" dir="auto">
                                {pickName(ing, i18n.language)}
                              </p>
                              <p className="text-[10px] text-muted-foreground font-mono mt-0.5" dir="ltr">
                                <bdi>{ingMacros.kcal} kcal · {ingMacros.protein}P / {ingMacros.carbs}C / {ingMacros.fat}F</bdi>
                              </p>
                            </div>
                            <NumberInput
                              inputMode="numeric"
                              step="10"
                              min="0"
                              max="9999"
                              value={ingPortion}
                              onChange={(e) => updatePortion(ing, e.target.value)}
                              dir="ltr"
                              className="h-9 w-20 px-2 rounded-md border border-input bg-background text-foreground text-sm font-mono text-center"
                            />
                            <span className="text-xs text-muted-foreground">g</span>
                          </div>
                        )
                      })}
                    </div>
                  )}
                  {selected && !recipeFood && natural && (
                    <div className="px-3 pb-3 pt-0 flex items-center gap-2">
                      <NumberInput
                        inputMode="decimal"
                        step="1"
                        min="0"
                        max="99"
                        value={count}
                        onChange={(e) => {
                          const n = Number(e.target.value)
                          updatePortion(food, Number.isFinite(n) ? n * food.grams_per_unit : 0)
                        }}
                        dir="ltr"
                        className="h-10 w-20 px-3 rounded-md border border-input bg-background text-foreground text-base font-mono text-center"
                      />
                      <span className="text-sm text-foreground font-medium">
                        {unitLabel(food, count, i18n.language)}
                      </span>
                      <span className="text-[11px] text-muted-foreground font-mono ms-auto" dir="ltr">
                        = {Math.round(portionG)} g
                      </span>
                    </div>
                  )}
                  {selected && !recipeFood && !natural && (
                    <div className="px-3 pb-3 pt-0 flex items-center gap-2">
                      <NumberInput
                        inputMode="numeric"
                        step="5"
                        min="0"
                        max="9999"
                        value={portionG}
                        onChange={(e) => updatePortion(food, e.target.value)}
                        dir="ltr"
                        className="h-10 w-24 px-3 rounded-md border border-input bg-background text-foreground text-base font-mono text-center"
                      />
                      <span className="text-sm text-foreground font-medium">
                        {unitLabel(food, 0, i18n.language)}
                      </span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        <button
          type="button"
          onClick={onSwitchToManual}
          className="mt-2 w-full text-start text-xs text-app-primary-500 hover:text-app-primary-600 font-medium flex items-center gap-1 px-1"
        >
          <Plus className="w-3 h-3" />
          {t('phoenixTracker.meals.builder.addCustom')}
        </button>
      </div>

      {/* Sticky bottom action bar — totals + cancel/save pinned above
          the floating tab bar so the user can save from anywhere in
          the (often long) food list without scrolling to the bottom.
          - Negative margins extend the bar to the parent Card's edges
            (the Card has p-4).
          - `bottom: calc(env(safe-area-inset-bottom) + 4.5rem)` clears
            the floating nav on mobile; on desktop where there's no
            nav the bar still pins just above the viewport bottom,
            harmless.
          - z-10 keeps it above the food list while pinned. */}
      <div
        className="sticky -mx-4 -mb-4 px-4 pt-3 pb-4 bg-card border-t border-border z-10 space-y-3"
        style={{ bottom: 'calc(env(safe-area-inset-bottom) + 4.5rem)' }}
      >
        <div className="rounded-lg border border-app-primary-500/30 bg-app-primary-500/5 p-3">
          <div className="flex items-baseline justify-between mb-0.5">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
              {t('phoenixTracker.meals.builder.totalLabel')}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {t('phoenixTracker.meals.builder.itemsCount', { count: items.length })}
            </p>
          </div>
          <p className="text-2xl font-bold font-mono text-app-primary-500" dir="ltr">
            {totals.kcal}
            <span className="text-sm font-normal text-muted-foreground ms-1.5">
              {t('phoenixTracker.meals.kcalUnit')}
            </span>
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5 font-mono" dir="ltr">
            {round1(totals.protein)}P / {round1(totals.carbs)}C / {round1(totals.fat)}F
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="flex-1"
          >
            {t('phoenixTracker.meals.form.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={submitting || items.length === 0}
            className="flex-1 bg-app-primary-500 hover:bg-app-primary-600 text-white"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin me-2" />
                {t('phoenixTracker.meals.form.saving')}
              </>
            ) : (
              t('phoenixTracker.meals.form.save')
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
